// "Sessions by Channel" vertical column chart — AA GA4 Traffic band
// reference screenshot (2026-08-09): Direct / Organic Search / Paid Search /
// Referral / Paid Other / Unassigned / Organic Social / Paid Social as
// vertical bars with rotated x-axis labels.
//
// A genuinely different shape from EntityClicksBarChart's horizontal bars,
// not a config toggle on it — that component's whole design is a coral
// "value" segment stacked against a muted "remainder" segment padded out to
// a shared max, so every row's total bar width is identical and the right-
// aligned value label lands at a fixed edge regardless of that row's own
// value (see its file docstring). A vertical column chart has no equivalent
// padding need (the Y-axis already gives every bar a shared scale) and no
// value-label-hugs-the-bar-end problem, so forking that stacked-pair
// machinery here would just be dead code carried along for the ride. Same
// underlying data model as EntityClicksBarChart though (label/value pairs)
// and the same `recharts` bake-off lock (no exceptions for a single chart),
// so this stays a thin, independent BarChart config rather than a new
// charting approach.
//
// Coral fill (Core primary accent, deep azure) — AA's own reference uses a
// dark teal hue here, which this repo's Core rebrand repurposed for the
// neutral-grey secondary slot; never reproduce that literal hue (CLAUDE.md).

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import type { LabeledCount } from "@/types/clientAnalytics";
import { formatCompactNum, formatNum } from "@/components/client-analytics/primitives";

export interface TrafficChannelBarChartProps {
  data: LabeledCount[];
  height?: number;
  emptyMessage?: string;
}

function ChannelTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  const label = (point.payload as { label: string } | undefined)?.label;
  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-dash-context shadow-sm">
      <div className="font-medium text-foreground">{label}</div>
      <div className="text-muted-foreground">
        {formatNum(typeof point.value === "number" ? point.value : null)} sessions
      </div>
    </div>
  );
}

export function TrafficChannelBarChart({
  data,
  height = 260,
  emptyMessage = "Not enough data yet",
}: TrafficChannelBarChartProps) {
  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }
  // Descending by value, matching AA's own reference ordering (Direct
  // tallest, Paid Social shortest) rather than whatever order the by_channel
  // pull happens to return.
  const sorted = [...data].sort((a, b) => b.value - a.value);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={sorted} margin={{ top: 8, right: 8, bottom: 44, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
          angle={-40}
          textAnchor="end"
          interval={0}
          height={54}
          axisLine={{ stroke: "var(--color-border)" }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v: number) => formatCompactNum(v)}
          tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
          width={44}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip cursor={{ fill: "var(--color-muted)" }} content={<ChannelTooltip />} />
        <Bar dataKey="value" fill="var(--color-coral)" radius={[4, 4, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  );
}
